import React, { useLayoutEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { Container, Eyebrow, Display, Lead, useInViewPlayback, useReveal } from "./ui";
import { AwardBadge } from "./Hero";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";
import { reveal, marquee, manifesto, crisis, nameMeaning } from "../../pages/landingContent";
import { posterFor } from "../../vid/posters";

/* ---------- Video that opens up as you scroll ---------- */

const RevealWrap = styled.section`
  padding: 24px 0 96px;

  /* How it works page (Josh, 29 Sep 2026): the whole card on one screen, clear of the menu. */
  &.fit {
    padding: 104px 0 40px;
  }
  &.fit .frame {
    max-height: calc(100svh - 150px);
  }
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 86vh;
  border-radius: 28px;
  overflow: hidden;
  background: ${colors.sand};
  box-shadow: 0 60px 120px -60px rgba(42, 35, 30, 0.55);
  transform-origin: 50% 0%;
  will-change: transform;

  @media (max-width: 659px) {
    aspect-ratio: 4 / 5;
  }

  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: saturate(0.9) sepia(0.05);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 45%, rgba(30, 42, 34, 0.72) 100%);
  }
`;

const Caption = styled.div`
  position: absolute;
  z-index: 2;
  left: 24px;
  right: 24px;
  bottom: 24px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;

  @media ${media.medium} {
    left: 48px;
    right: 48px;
    bottom: 44px;
  }
`;

const CaptionTitle = styled.p`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(30px, 4.4vw, 64px);
  line-height: 1.02;
  letter-spacing: -0.03em;
  color: ${colors.linen};
  max-width: 760px;

  em {
    font-style: normal;
    color: ${colors.copperLight};
  }
`;

export const VideoReveal = ({ fit = false }) => {
  const frame = useRef(null);
  const video = useRef(null);
  useInViewPlayback(video);

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        frame.current,
        { scale: 0.84, borderRadius: 64 },
        {
          scale: 1,
          borderRadius: 28,
          ease: "none",
          scrollTrigger: { trigger: frame.current, start: "top 95%", end: "top 20%", scrub: true },
        }
      );
      gsap.fromTo(
        video.current,
        { scale: 1.25 },
        {
          scale: 1,
          ease: "none",
          scrollTrigger: { trigger: frame.current, start: "top bottom", end: "bottom top", scrub: true },
        }
      );
    }, frame);
    return () => ctx.revert();
  }, []);

  return (
    <RevealWrap className={fit ? "fit" : ""} aria-label={reveal.kicker}>
      <Container>
        <Frame ref={frame} className="frame">
          <video ref={video} src={reveal.video} poster={posterFor(reveal.video)} muted loop playsInline preload="metadata" />
          <Caption>
            <div>
              <Eyebrow $color={colors.linen} $onDark>
                {reveal.kicker}
              </Eyebrow>
              <CaptionTitle style={{ marginTop: 14 }}>{reveal.line}</CaptionTitle>
            </div>
          </Caption>
        </Frame>
      </Container>
    </RevealWrap>
  );
};

/* ---------- Marquee ---------- */

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const MarqueeWrap = styled.div`
  border-top: 1px solid ${colors.stone};
  border-bottom: 1px solid ${colors.stone};
  background: ${colors.linen};
  padding: 22px 0;
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
`;

const Track = styled.div`
  display: flex;
  width: max-content;
  animation: ${scroll} 48s linear infinite;

  ${MarqueeWrap}:hover & {
    animation-play-state: paused;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Item = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: ${fonts.display};
  font-size: clamp(26px, 3.2vw, 44px);
  font-weight: 300;
  letter-spacing: -0.02em;
  white-space: nowrap;
  color: ${(p) => (p.$alt ? colors.pebble : colors.ink)};

  &::after {
    content: "✦";
    font-size: 0.5em;
    color: ${colors.copperGlow};
    margin: 0 36px;
    font-style: normal;
  }
`;

export const Marquee = () => {
  const items = [...marquee, ...marquee];
  return (
    <MarqueeWrap aria-label={marquee.join(", ")}>
      <Track aria-hidden="true">
        {items.map((label, i) => (
          <Item key={`${label}-${i}`} $alt={i % 2 === 1}>
            {label}
          </Item>
        ))}
      </Track>
    </MarqueeWrap>
  );
};

/* ---------- Manifesto: words ink in as you scroll ---------- */

const ManifestoWrap = styled.section`
  padding: 140px 0;

  @media ${media.large} {
    padding: 200px 0;
  }
`;

const ManifestoText = styled.p`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(30px, 4.6vw, 68px);
  line-height: 1.12;
  letter-spacing: -0.025em;
  max-width: 1120px;
  color: ${colors.ink};
  margin-top: 28px;

  span {
    color: ${colors.ink};
  }
  .accent {
    font-style: normal;
  }
`;

const ACCENT_WORDS = ["into", "neurological,", "grow."];

export const Manifesto = () => {
  const scope = useRef(null);
  const words = manifesto.split(" ");

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".word",
        { color: colors.stone },
        {
          color: (i, el) => (el.classList.contains("accent") ? colors.copper : colors.ink),
          stagger: 0.1,
          ease: "none",
          scrollTrigger: { trigger: scope.current, start: "top 75%", end: "bottom 55%", scrub: true },
        }
      );
    }, scope);
    return () => ctx.revert();
  }, []);

  return (
    <ManifestoWrap ref={scope}>
      <Container>
        <Eyebrow $color={colors.ocean}>Why vimbal</Eyebrow>
        <ManifestoText>
          {words.map((word, i) => {
            const accent = ACCENT_WORDS.includes(word);
            return (
              <React.Fragment key={i}>
                <span
                  className={`word${accent ? " accent" : ""}`}
                  style={accent ? { color: colors.copper } : undefined}
                >
                  {word}
                </span>{" "}
              </React.Fragment>
            );
          })}
        </ManifestoText>
      </Container>
    </ManifestoWrap>
  );
};

/* ---------- The cognitive crisis ---------- */

const CrisisWrap = styled.section`
  padding: 120px 0 0;

  /* New home page: the next section follows directly, so this one needs its own bottom space. */
  &.spaced {
    padding-bottom: 140px;
  }
  /* Phones: the award sits in the flow above the eyebrow, so the top space makes room for it. */
  @media (max-width: 659px) {
    &.spaced {
      padding-top: 64px;
    }
  }
`;

const CrisisHead = styled.div`
  position: relative;
  display: grid;
  gap: 24px;
  margin-bottom: 48px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: end;
  }

  /* The award sits above the header on the right, its bottom level with the top of the eyebrow,
     on one line (Josh, 30 Sep 2026); the header keeps its place. Phones: in the flow above the
     eyebrow, wrapping inside the pill. */
  .award {
    position: absolute;
    right: 0;
    bottom: calc(100% - 5px);
    width: auto;
    margin: 0;
  }
  @media (max-width: 659px) {
    .award {
      position: static;
      grid-row: 1;
      justify-self: start;
    }
  }
`;

const CrisisGrid = styled.div`
  display: grid;
  gap: 16px;

  @media ${media.medium} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const CrisisStat = styled.div`
  position: relative;
  overflow: hidden;
  padding: 30px 26px;
  border-radius: 24px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};

  &::after {
    content: "";
    position: absolute;
    right: -40px;
    top: -40px;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(154, 78, 46, 0.14), transparent);
  }
  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(52px, 5vw, 76px);
    line-height: 1;
    letter-spacing: -0.045em;
    color: ${colors.terracotta};
  }
  p {
    margin-top: 14px;
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.5;
    color: ${colors.ink};
  }
  small {
    display: block;
    margin-top: 14px;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${colors.pebble};
  }
`;

export const Crisis = ({ award = false }) => {
  const scope = useRef(null);
  useReveal(scope);
  return (
    <CrisisWrap ref={scope} className={award ? "spaced" : ""} aria-labelledby="crisis-title">
      <Container>
        <CrisisHead>
          <div data-reveal>
            <Eyebrow $color={colors.terracotta}>{crisis.eyebrow}</Eyebrow>
            <Display id="crisis-title" style={{ marginTop: 20 }}>
              {crisis.titleLead} <em>{crisis.titleEmphasis}</em>
            </Display>
          </div>
          {award && <AwardBadge className="award" />}
          <Lead data-reveal="0.1">{crisis.body}</Lead>
        </CrisisHead>
        <CrisisGrid>
          {crisis.stats.map((st, i) => (
            <CrisisStat key={st.value} data-reveal={i * 0.08}>
              <b>{st.value}</b>
              <p>{st.label}</p>
              <small>Source: {st.source}</small>
            </CrisisStat>
          ))}
        </CrisisGrid>
      </Container>
    </CrisisWrap>
  );
};

/* ---------- What vimbal stands for ---------- */

// Fills the screen on its own so the name gets a moment before the next section.
const NameWrap = styled.section`
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  padding: 96px 0;

  > * {
    width: 100%;
  }
`;

const NameInner = styled(Container)`
  position: relative;
  z-index: 1;
`;


const LetterGrid = styled.ol`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 36px;

  @media ${media.small} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media ${media.large} {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

const CardSlot = styled.li``;

const LetterCard = styled.div`
  position: relative;
  overflow: hidden;
  height: 100%;
  padding: 22px 20px 24px;
  border-radius: 24px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  box-shadow: 0 18px 40px -30px rgba(42, 35, 30, 0.45);
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), background 0.4s ease, border-color 0.4s ease;

  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 300;
    font-style: normal;
    font-size: clamp(64px, 7vw, 104px);
    line-height: 0.9;
    letter-spacing: -0.04em;
    color: ${(p) => p.$tone};
    transition: color 0.4s ease;
  }
  strong {
    display: block;
    margin-top: 14px;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 22px;
    letter-spacing: -0.015em;
    color: ${colors.ink};
    transition: color 0.4s ease;
  }
  span {
    display: block;
    margin-top: 6px;
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.45;
    color: ${colors.bark};
    transition: color 0.4s ease;
  }

  &:hover {
    transform: translateY(-6px);
    background: ${colors.forest};
    border-color: ${colors.forest};
  }
  &:hover b {
    color: ${colors.copperLight};
  }
  &:hover strong {
    color: ${colors.linen};
  }
  &:hover span {
    color: rgba(250, 247, 241, 0.75);
  }
`;

const FullName = styled.p`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(20px, 5.4vw, 26px);
  line-height: 1.15;
  letter-spacing: -0.015em;
  color: ${colors.ink};
  margin-top: 16px;

  /* From tablet up the line is sized in JS to span the full width of the cards below. */
  @media ${media.small} {
    white-space: nowrap;
  }

  em {
    font-style: normal;
    font-size: 1.3em;
    line-height: 1;
  }
`;

// Scales a single line of text so it exactly fills its container's width (tablet and up).
const useFitWidth = (ref) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const mq = window.matchMedia(media.small);
    const fit = () => {
      if (!mq.matches) {
        el.style.fontSize = "";
        return;
      }
      // Measure the text itself (not the box) at a reference size, then scale to fill.
      // Binary-search the largest size whose text still fits, so the last letter lands on the edge.
      const range = document.createRange();
      range.selectNodeContents(el);
      const available = el.clientWidth;
      const widthAt = (px) => {
        el.style.fontSize = `${px}px`;
        return range.getBoundingClientRect().width;
      };
      let lo = 8;
      let hi = 160;
      for (let step = 0; step < 20; step += 1) {
        const mid = (lo + hi) / 2;
        if (widthAt(mid) <= available) lo = mid;
        else hi = mid;
      }
      el.style.fontSize = `${lo}px`;
    };
    fit();
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (Math.abs(el.clientWidth - lastWidth) < 1) return;
      lastWidth = el.clientWidth;
      fit();
    });
    ro.observe(el);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    mq.addEventListener("change", fit);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      mq.removeEventListener("change", fit);
      window.removeEventListener("resize", fit);
    };
  }, [ref]);
};

const LETTER_TONES = [colors.ocean, colors.moss, colors.terracotta, colors.walnut, colors.copper, colors.ocean];

export const NameMeaning = () => {
  const scope = useRef(null);
  const lineRef = useRef(null);
  useReveal(scope);
  useFitWidth(lineRef);

  return (
    <NameWrap ref={scope} aria-labelledby="name-title">
      <NameInner>
        <div data-reveal>
          <Eyebrow $color={colors.moss}>{nameMeaning.eyebrow}</Eyebrow>
          <FullName id="name-title" ref={lineRef}>
            {nameMeaning.letters.map((l, i) => (
              <React.Fragment key={l.letter}>
                <em style={{ color: LETTER_TONES[i] }}>{l.letter}</em>
                {l.word.slice(1)}
                {i < nameMeaning.letters.length - 1 ? " " : ""}
              </React.Fragment>
            ))}
          </FullName>
        </div>
        <LetterGrid>
          {nameMeaning.letters.map((l, i) => (
            <CardSlot key={l.letter} data-reveal={i * 0.06}>
              <LetterCard $tone={LETTER_TONES[i]}>
                <b aria-hidden="true">{l.letter}</b>
                <strong>{l.word}</strong>
                <span>{l.body}</span>
              </LetterCard>
            </CardSlot>
          ))}
        </LetterGrid>
      </NameInner>
    </NameWrap>
  );
};
